import React, { createContext, useContext, useState } from 'react';
import { translations } from '../i18n/translations';
import { isLanguage, Language, LANGUAGES } from '../i18n/languages';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: typeof translations.en;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const BROWSER_LANG_MAP: [string, Language][] = [
  ['es', 'es'],
  ['en', 'en'],
  ['fr', 'fr'],
  ['de', 'de'],
  ['it', 'it'],
  ['da', 'da'],
];

function detectBrowserLanguage(): Language {
  if (typeof navigator === 'undefined') return 'en';
  const list =
    navigator.languages?.length > 0 ? [...navigator.languages] : [navigator.language];
  for (const code of list) {
    const low = (code || '').toLowerCase();
    for (const [prefix, lang] of BROWSER_LANG_MAP) {
      if (low.startsWith(prefix)) return lang;
    }
  }
  return 'en';
}

function readLangFromUrl(): Language | null {
  if (typeof window === 'undefined') return null;
  const params = new URLSearchParams(window.location.search);
  const lang = params.get('lang');
  return isLanguage(lang) ? lang : null;
}

function readInitialLanguage(): Language {
  if (typeof window === 'undefined') return 'en';
  const urlLang = readLangFromUrl();
  if (urlLang) {
    localStorage.setItem('language', urlLang);
    return urlLang;
  }
  const saved = localStorage.getItem('language');
  if (isLanguage(saved)) return saved;
  return detectBrowserLanguage();
}

export function parseUrlLanguage(lang: string | null): Language | null {
  return isLanguage(lang) ? lang : null;
}

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(readInitialLanguage);

  const setLanguage = (lang: Language) => {
    if (!LANGUAGES.includes(lang)) return;
    setLanguageState(lang);
    localStorage.setItem('language', lang);
  };

  const t = translations[language];

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export type { Language };
